import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../api';
import { useAuth } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface Paket {
  id: string;
  ad: string;
  kredi_miktari: number;
  fiyat: number;
  indirimli_fiyat: number | null;
  aciklama: string | null;
  populer: boolean;
  aktif: boolean;
}

const bos: Omit<Paket, 'id' | 'aktif'> = {
  ad: '', kredi_miktari: 0, fiyat: 0, indirimli_fiyat: null, aciklama: null, populer: false,
};

export default function KrediPaketleri() {
  usePageTitle('Kredi Paketleri');
  const { token } = useAuth() as any;
  const { isDark } = useTheme();
  const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const card      = isDark ? '#1a1a1a' : 'white';
  const cardAlt   = isDark ? '#1e1e1e' : '#f5f5f5';
  const border    = isDark ? '#2a2a2a' : '#eee';
  const borderSub = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec   = isDark ? '#999' : '#888';
  const textMuted = isDark ? '#666' : '#aaa';

  const [paketler, setPaketler] = useState<Paket[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [modal, setModal] = useState<{ open: boolean; paket: Partial<Paket> & typeof bos }>({
    open: false, paket: { ...bos },
  });
  const [kaydediyor, setKaydediyor] = useState(false);

  async function yukle() {
    setYukleniyor(true);
    const r = await fetch(`${BASE_URL}/api/krediler/paketler/yonetim`, { headers: h });
    setPaketler(await r.json());
    setYukleniyor(false);
  }

  useEffect(() => { yukle(); }, []);

  function yeniAc() { setModal({ open: true, paket: { ...bos } }); }
  function duzenleAc(p: Paket) { setModal({ open: true, paket: { ...p } }); }

  async function kaydet() {
    const p = modal.paket;
    if (!p.ad || !p.kredi_miktari || !p.fiyat) return alert('Ad, kredi ve fiyat zorunlu');
    setKaydediyor(true);
    const isEdit = !!(p as any).id;
    const url = isEdit
      ? `${BASE_URL}/api/krediler/paketler/${(p as any).id}`
      : `${BASE_URL}/api/krediler/paketler`;
    await fetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      headers: h,
      body: JSON.stringify({
        ad: p.ad, kredi_miktari: Number(p.kredi_miktari), fiyat: Number(p.fiyat),
        indirimli_fiyat: p.indirimli_fiyat ? Number(p.indirimli_fiyat) : null,
        aciklama: p.aciklama || null, populer: p.populer, aktif: (p as any).aktif !== false,
      }),
    });
    setKaydediyor(false);
    setModal({ open: false, paket: { ...bos } });
    yukle();
  }

  async function sil(id: string) {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Bu paketi silmek istiyor musunuz?')) return;
    await fetch(`${BASE_URL}/api/krediler/paketler/${id}`, { method: 'DELETE', headers: h });
    yukle();
  }

  async function aktifToggle(p: Paket) {
    await fetch(`${BASE_URL}/api/krediler/paketler/${p.id}`, {
      method: 'PUT', headers: h,
      body: JSON.stringify({ ...p, aktif: !p.aktif }),
    });
    yukle();
  }

  const set = (k: string, v: any) => setModal(m => ({ ...m, paket: { ...m.paket, [k]: v } }));

  const td: React.CSSProperties = { padding: '12px 14px', fontSize: 14, verticalAlign: 'middle', color: textPrimary };
  const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: isDark ? '#bbb' : '#555', marginBottom: 4, marginTop: 12 };
  const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', border: `1px solid ${border}`, borderRadius: 7, fontSize: 14, boxSizing: 'border-box', outline: 'none' };

  return (
    <div style={{ padding: 32, maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: textPrimary }}>Kredi Paketleri</h2>
          <p style={{ margin: '4px 0 0', color: textSec, fontSize: 13 }}>
            Mobil uygulamada görünen paketleri buradan yönetin
          </p>
        </div>
        <button onClick={yeniAc} style={{ background: '#3C3489', color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Yeni Paket</button>
      </div>

      {yukleniyor ? (
        <p style={{ color: textMuted }}>Yükleniyor…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: card, borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <thead>
            <tr style={{ background: cardAlt, fontSize: 12, color: textSec, textTransform: 'uppercase' }}>
              {['Ad', 'Kredi', 'Fiyat', 'İndirimli', 'Açıklama', 'Popüler', 'Durum', ''].map(c => (
                <th key={c} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: textSec }}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paketler.map((p, i) => (
              <tr key={p.id} style={{ borderTop: i ? `1px solid ${borderSub}` : undefined }}>
                <td style={td}><span style={{ fontWeight: 600 }}>{p.ad}</span></td>
                <td style={td}><span style={{ fontWeight: 700, color: '#3C3489' }}>{p.kredi_miktari}</span></td>
                <td style={td}>₺{p.fiyat}</td>
                <td style={td}>{p.indirimli_fiyat ? <span style={{ color: '#16a34a', fontWeight: 600 }}>₺{p.indirimli_fiyat}</span> : <span style={{ color: textMuted }}>—</span>}</td>
                <td style={{ ...td, color: textSec, fontSize: 12 }}>{p.aciklama || '—'}</td>
                <td style={td}>
                  {p.populer ? <span style={{ background: isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE', color: '#3C3489', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>Popüler</span> : <span style={{ color: textMuted }}>—</span>}
                </td>
                <td style={td}>
                  <button onClick={() => aktifToggle(p)} style={{
                    border: 'none', borderRadius: 12, padding: '3px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
                    background: p.aktif ? (isDark ? 'rgba(16,185,129,0.15)' : '#dcfce7') : (isDark ? 'rgba(228,75,74,0.12)' : '#fee2e2'),
                    color: p.aktif ? '#16a34a' : '#dc2626',
                  }}>
                    {p.aktif ? 'Aktif' : 'Pasif'}
                  </button>
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button onClick={() => duzenleAc(p)} style={{ background: 'transparent', color: '#3C3489', border: '1px solid #3C3489', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Düzenle</button>
                  <button onClick={() => sil(p.id)} style={{ background: 'transparent', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Sil</button>
                </td>
              </tr>
            ))}
            {!paketler.length && (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: textMuted }}>Henüz paket yok</td></tr>
            )}
          </tbody>
        </table>
      )}

      {modal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: card, borderRadius: 14, padding: 28, width: 460, maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, color: textPrimary }}>
              {(modal.paket as any).id ? 'Paketi Düzenle' : 'Yeni Paket'}
            </h3>

            <label style={lbl}>Paket Adı *</label>
            <input style={inp} value={modal.paket.ad} onChange={e => set('ad', e.target.value)} placeholder="ör. Başlangıç" />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={lbl}>Kredi Miktarı *</label>
                <input style={inp} type="number" value={modal.paket.kredi_miktari || ''} onChange={e => set('kredi_miktari', e.target.value)} placeholder="10" />
              </div>
              <div>
                <label style={lbl}>Normal Fiyat (₺) *</label>
                <input style={inp} type="number" value={modal.paket.fiyat || ''} onChange={e => set('fiyat', e.target.value)} placeholder="900" />
              </div>
            </div>

            <label style={lbl}>İndirimli Fiyat (₺) — opsiyonel</label>
            <input style={inp} type="number" value={modal.paket.indirimli_fiyat || ''} onChange={e => set('indirimli_fiyat', e.target.value || null)} placeholder="850" />

            <label style={lbl}>Açıklama — opsiyonel</label>
            <input style={inp} value={modal.paket.aciklama || ''} onChange={e => set('aciklama', e.target.value || null)} placeholder="ör. %5 indirim" />

            <div style={{ display: 'flex', gap: 20, marginTop: 4, marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: textPrimary }}>
                <input type="checkbox" checked={!!modal.paket.populer} onChange={e => set('populer', e.target.checked)} />
                Popüler (öne çıkar)
              </label>
              {(modal.paket as any).id && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: textPrimary }}>
                  <input type="checkbox" checked={(modal.paket as any).aktif !== false} onChange={e => set('aktif', e.target.checked)} />
                  Aktif
                </label>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal({ open: false, paket: { ...bos } })} style={{ background: isDark ? '#333' : '#aaa', color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>İptal</button>
              <button onClick={kaydet} disabled={kaydediyor} style={{ background: '#3C3489', color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {kaydediyor ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
